import { useEffect, useMemo, useRef, useState } from "react";
import { wooCall } from "../lib/api.js";
import PhotoLightbox from "./PhotoLightbox.jsx";

// 🛍️ 網店 — the customer portal's shop tab for shops with WooCommerce on.
// Web twin of the iOS customer app's ShopProductsView + CartView: browse
// (categories, search, load more) → product (photos, options, qty) → cart
// → contact details → WooCommerce creates a pending order → the customer
// pays on the shop's own WooCommerce "pay for order" page (new tab).
// Everything goes through the woocommerce edge fn; the shop's WC keys never
// reach the browser.
//
// Product descriptions come from the shop's WordPress as HTML. They are
// rendered as PLAIN TEXT (never innerHTML): this page runs on pettobook.com
// with the customer's session, so foreign markup must not execute here.

const PER = 24;
const cartKey = (tid) => "petos:wc-cart:" + tid;
const loadCart = (tid) => { try { const v = JSON.parse(localStorage.getItem(cartKey(tid)) || "[]"); return Array.isArray(v) ? v : []; } catch { return []; } };
const saveCart = (tid, lines) => { try { localStorage.setItem(cartKey(tid), JSON.stringify(lines)); } catch { /* private mode */ } };
const money = (n) => { const x = Number(n) || 0; return "$" + (Number.isInteger(x) ? x : x.toFixed(2)); };
const inStock = (p) => (p?.stock_status || "instock") !== "outofstock";
const isBackorder = (p) => p?.stock_status === "onbackorder";

function htmlToText(html) {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(String(html).replace(/<\s*br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h\d)>/gi, "\n"), "text/html");
    return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  } catch { return String(html).replace(/<[^>]+>/g, " ").trim(); }
}

export default function CustomerShopStore({ tenantId, tenantName, user, membership, T }) {
  const t = T || ((zh) => zh);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currency, setCurrency] = useState("HKD");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [cart, setCart] = useState(() => loadCart(tenantId));
  const [cartOpen, setCartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const reqId = useRef(0);

  useEffect(() => { saveCart(tenantId, cart); }, [tenantId, cart]);

  useEffect(() => {
    wooCall({ tenant: tenantId, op: "categories" }).then((r) => setCats(r?.categories || [])).catch(() => setCats([]));
  }, [tenantId]);

  // First page whenever the category or search changes.
  useEffect(() => {
    const my = ++reqId.current;
    setLoading(true); setErr("");
    wooCall({ tenant: tenantId, op: "products", per_page: PER, page: 1, search: query, category: cat })
      .then((r) => {
        if (my !== reqId.current) return;
        setProducts(r?.products || []); setPage(1); setTotalPages(r?.total_pages || 1);
        if (r?.currency) setCurrency(r.currency);
      })
      .catch((e) => {
        if (my !== reqId.current) return;
        // 412 = the shop's WooCommerce link isn't set up; don't show customers the raw server text.
        setErr(e?.status === 412 ? t("網店暫時未開放，請稍後再試。", "The shop isn't open yet — please try again later.") : t("未能載入產品，請稍後再試。", "Couldn't load products — please try again."));
      })
      .finally(() => { if (my === reqId.current) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, cat, query]);

  const loadMore = async () => {
    if (loading || page >= totalPages) return;
    const my = ++reqId.current;
    setLoading(true);
    try {
      const r = await wooCall({ tenant: tenantId, op: "products", per_page: PER, page: page + 1, search: query, category: cat });
      if (my !== reqId.current) return;
      setProducts((xs) => [...xs, ...(r?.products || []).filter((p) => !xs.some((x) => x.id === p.id))]);
      setPage(page + 1); setTotalPages(r?.total_pages || totalPages);
    } catch (e) { if (my === reqId.current) setErr(e.message || ""); }
    finally { if (my === reqId.current) setLoading(false); }
  };

  const addToCart = (line) => {
    setCart((xs) => {
      const i = xs.findIndex((x) => x.key === line.key);
      if (i < 0) return [...xs, line];
      const next = xs.slice(); next[i] = { ...next[i], quantity: next[i].quantity + line.quantity }; return next;
    });
  };
  const cartCount = cart.reduce((n, l) => n + l.quantity, 0);

  const catName = cat == null ? t("全部", "All") : htmlToText((cats.find((c) => c.id === cat) || {}).name || "");

  return (
    <div style={{ paddingTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ fontWeight: 900, fontSize: 22, margin: 0, color: "#4A4A68", flex: 1 }}>🛍️ {t("網店", "Shop")}</h2>
        <button type="button" onClick={() => setCartOpen(true)} aria-label={t("購物車", "Cart")}
          style={{ position: "relative", border: "none", background: "var(--c-primary)", color: "#fff", borderRadius: 99, padding: "10px 16px", fontWeight: 900, fontSize: 14, cursor: "pointer" }}>
          🛒 {t("購物車", "Cart")}
          {cartCount > 0 && <span style={{ position: "absolute", top: -6, right: -6, background: "#111", color: "#fff", borderRadius: 99, minWidth: 20, height: 20, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{cartCount}</span>}
        </button>
      </div>

      {/* ☰ opens the category tree on the left, like the iOS app's sidebar. */}
      <form onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }} style={{ display: "flex", gap: 8 }}>
        {cats.length > 0 && (
          <button type="button" onClick={() => setMenuOpen(true)} aria-label={t("產品分類", "Categories")}
            style={{ flexShrink: 0, width: 46, height: 46, borderRadius: 14, border: "2px solid var(--c-light)", background: "#fff", color: "var(--c-primary)", fontSize: 20, fontWeight: 900, cursor: "pointer" }}>☰</button>
        )}
        <input value={search} onChange={(e) => { setSearch(e.target.value); if (!e.target.value) setQuery(""); }} placeholder={t("搜尋產品…", "Search products…")} type="search"
          style={{ flex: 1, minWidth: 0, border: "2px solid var(--c-light)", borderRadius: 99, padding: "11px 16px", fontSize: 15, fontWeight: 600, outline: "none", background: "#FFFDFD", color: "#4A4A68" }} />
      </form>
      {cats.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800, color: "#8A8A9E" }}>
          <button type="button" onClick={() => setMenuOpen(true)} style={{ border: "none", background: "var(--c-lighter)", color: "var(--c-primary)", borderRadius: 99, padding: "6px 12px", fontWeight: 900, fontSize: 13, cursor: "pointer" }}>📂 {catName}</button>
          {cat != null && <button type="button" onClick={() => setCat(null)} style={{ border: "none", background: "none", color: "#8A8A9E", fontWeight: 800, cursor: "pointer", fontSize: 12 }}>✕ {t("顯示全部", "Show all")}</button>}
        </div>
      )}
      {menuOpen && <CategoryDrawer cats={cats} current={cat} t={t} onPick={(id) => { setCat(id); setMenuOpen(false); }} onClose={() => setMenuOpen(false)} />}

      {err && <div style={{ background: "#FEE2E2", color: "#991B1B", borderRadius: 12, padding: "10px 12px", fontSize: 13, fontWeight: 800 }}>{err}</div>}

      {!loading && !err && products.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 12px", color: "#8A8A9E", fontWeight: 800, fontSize: 14 }}>
          {query || cat ? t("搵唔到產品，試下其他關鍵字或者揀「全部」。", "No products — try another search or pick 「All」.") : t("店舖暫時未有產品。", "No products yet.")}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
        {products.map((p) => (
          <button key={p.id} type="button" onClick={() => setOpenId(p.id)}
            style={{ textAlign: "left", background: "#fff", border: "2px solid var(--c-light)", borderRadius: 16, padding: 0, overflow: "hidden", cursor: "pointer", display: "flex", flexDirection: "column" }}>
            <div style={{ position: "relative", aspectRatio: "1", background: "var(--c-lighter)" }}>
              {p.images?.[0]?.src && <img src={p.images[0].src} alt={p.images[0].alt || ""} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: inStock(p) ? 1 : 0.5 }} />}
              <div style={{ position: "absolute", top: 6, left: 6, display: "flex", gap: 4 }}>
                {p.on_sale && <Tag bg="#E11D48">{t("減價", "SALE")}</Tag>}
                {isBackorder(p) && <Tag bg="#7C3AED">{t("接受預訂", "PRE-ORDER")}</Tag>}
                {!inStock(p) && <Tag bg="#6B7280">{t("售罄", "SOLD OUT")}</Tag>}
              </div>
            </div>
            <div style={{ padding: "8px 10px 10px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#4A4A68", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{htmlToText(p.name)}</div>
              <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 15, fontWeight: 900, color: "var(--c-primary)" }}>{p.type === "variable" ? t("由 ", "From ") : ""}{money(p.price)}</span>
                {p.on_sale && p.regular_price && <span style={{ fontSize: 11, color: "#9CA3AF", textDecoration: "line-through", fontWeight: 700 }}>{money(p.regular_price)}</span>}
              </div>
            </div>
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: "center", color: "#AFAFC4", fontWeight: 800, padding: 16 }}>{t("載入中…", "Loading…")}</div>}
      {!loading && page < totalPages && (
        <button type="button" onClick={loadMore} style={{ border: "2px solid var(--c-light)", background: "#fff", borderRadius: 99, padding: "12px", fontWeight: 900, color: "var(--c-primary)", cursor: "pointer" }}>{t("載入更多", "Load more")}</button>
      )}
      <p style={{ fontSize: 11, color: "#AFAFC4", textAlign: "center", fontWeight: 700, margin: "4px 0 0" }}>{t("價錢以", "Prices in ")}{currency}{t(" 計，運費同稅項會喺付款頁計算。", ". Shipping and tax are calculated at payment.")}</p>

      {openId != null && <ProductSheet tenantId={tenantId} id={openId} t={t} onClose={() => setOpenId(null)} onAdd={(line) => { addToCart(line); setOpenId(null); }} />}
      {cartOpen && <CartSheet tenantId={tenantId} tenantName={tenantName} cart={cart} setCart={setCart} user={user} membership={membership} t={t} onClose={() => setCartOpen(false)} />}
    </div>
  );
}

// Left slide-in category tree. Tap a name to filter; tap ▸ to open its
// sub-categories (same tree as the iOS app's sidebar, 3 levels deep).
function CategoryDrawer({ cats, current, t, onPick, onClose }) {
  const ids = useMemo(() => new Set(cats.map((c) => c.id)), [cats]);
  const kids = (pid) => cats.filter((c) => c.parent === pid);
  const top = cats.filter((c) => !c.parent || !ids.has(c.parent));
  // Open the branch that holds the current category.
  const [open, setOpen] = useState(() => {
    const s = new Set(); let c = cats.find((x) => x.id === current);
    while (c && c.parent && ids.has(c.parent)) { s.add(c.parent); c = cats.find((x) => x.id === c.parent); }
    return s;
  });
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = prev; };
  }, [onClose]);
  const row = (c, depth) => {
    const ch = depth < 2 ? kids(c.id) : [];
    const isOpen = open.has(c.id);
    const on = current === c.id;
    return (
      <div key={c.id}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, paddingLeft: 8 + depth * 16 }}>
          <button type="button" onClick={() => onPick(c.id)}
            style={{ flex: 1, minWidth: 0, textAlign: "left", border: "none", cursor: "pointer", borderRadius: 12, padding: "11px 12px", fontSize: depth ? 13.5 : 14.5, fontWeight: on ? 900 : 800,
              background: on ? "var(--c-lighter)" : "transparent", color: on ? "var(--c-primary)" : "#4A4A68", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {depth === 0 ? "📁 " : ch.length ? "📂 " : "• "}{htmlToText(c.name)}{c.count ? <span style={{ color: "#AFAFC4", fontWeight: 700, fontSize: 12 }}> ({c.count})</span> : null}
          </button>
          {ch.length > 0 && (
            <button type="button" aria-label={isOpen ? t("收起", "Collapse") : t("展開", "Expand")} aria-expanded={isOpen}
              onClick={() => setOpen((s) => { const n = new Set(s); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })}
              style={{ width: 36, height: 36, border: "none", background: "none", cursor: "pointer", color: "#8A8A9E", fontSize: 14, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</button>
          )}
        </div>
        {isOpen && ch.map((x) => row(x, depth + 1))}
      </div>
    );
  };
  return (
    <div role="dialog" aria-modal="true" aria-label={t("產品分類", "Categories")} onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 330, background: "rgba(20,18,40,0.45)" }}>
      <div className="petos-cat-drawer" onClick={(e) => e.stopPropagation()}
        style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "min(320px, 84vw)", background: "#FCFCFF", boxShadow: "8px 0 30px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column",
          paddingTop: "env(safe-area-inset-top)", animation: "petosDrawerIn .2s ease-out" }}>
        <style>{"@keyframes petosDrawerIn{from{transform:translateX(-100%)}to{transform:none}}@media (prefers-reduced-motion: reduce){.petos-cat-drawer{animation:none!important}}"}</style>
        <div style={{ display: "flex", alignItems: "center", padding: "16px 14px 10px 18px" }}>
          <div style={{ flex: 1, fontWeight: 900, fontSize: 18, color: "#4A4A68" }}>☰ {t("產品分類", "Categories")}</div>
          <button type="button" onClick={onClose} aria-label={t("關閉", "Close")} style={{ width: 34, height: 34, borderRadius: "50%", border: "none", background: "var(--c-lighter)", color: "var(--c-primary)", fontSize: 16, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "0 8px 24px" }}>
          <button type="button" onClick={() => onPick(null)}
            style={{ width: "calc(100% - 8px)", marginLeft: 8, textAlign: "left", border: "none", cursor: "pointer", borderRadius: 12, padding: "11px 12px", fontSize: 14.5, fontWeight: current == null ? 900 : 800,
              background: current == null ? "var(--c-lighter)" : "transparent", color: current == null ? "var(--c-primary)" : "#4A4A68" }}>▦ {t("全部產品", "All products")}</button>
          {top.map((c) => row(c, 0))}
        </div>
      </div>
    </div>
  );
}

function Tag({ bg, children }) {
  return <span style={{ background: bg, color: "#fff", fontSize: 10, fontWeight: 900, borderRadius: 6, padding: "2px 6px" }}>{children}</span>;
}

function Sheet({ title, onClose, children, footer }) {
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 320, background: "rgba(20,18,40,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#FCFCFF", width: "100%", maxWidth: 560, maxHeight: "92dvh", borderRadius: "24px 24px 0 0", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--c-light)" }}>
          <div style={{ flex: 1, fontWeight: 900, fontSize: 17, color: "#4A4A68", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
          <button type="button" onClick={onClose} aria-label="關閉" style={{ width: 34, height: 34, borderRadius: "50%", border: "none", background: "var(--c-lighter)", color: "var(--c-primary)", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: 16, flex: 1 }}>{children}</div>
        {footer && <div style={{ padding: "12px 16px calc(12px + env(safe-area-inset-bottom))", borderTop: "1px solid var(--c-light)", background: "#fff" }}>{footer}</div>}
      </div>
    </div>
  );
}

const bigBtn = (disabled) => ({ width: "100%", padding: "15px", borderRadius: 99, border: "none", background: "linear-gradient(135deg,var(--c-grad1),var(--c-primary))", color: "#fff", fontWeight: 900, fontSize: 15, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1 });

function ProductSheet({ tenantId, id, t, onClose, onAdd }) {
  const [p, setP] = useState(null);
  const [vars, setVars] = useState([]);
  const [varsLoaded, setVarsLoaded] = useState(false);
  const [pick, setPick] = useState({});
  const [qty, setQty] = useState(1);
  const [err, setErr] = useState("");
  const [lb, setLb] = useState(null);

  useEffect(() => {
    let alive = true;
    wooCall({ tenant: tenantId, op: "product", id }).then((r) => {
      if (!alive) return;
      setP(r?.product || null);
      if (r?.product?.type === "variable") {
        wooCall({ tenant: tenantId, op: "variations", id })
          .then((v) => { if (alive) setVars(v?.variations || []); })
          .catch(() => {})
          .finally(() => { if (alive) setVarsLoaded(true); });
      }
    }).catch((e) => { if (alive) setErr(e.message || t("未能載入產品", "Couldn't load product")); });
    return () => { alive = false; };
  }, [tenantId, id, t]);

  const attrs = (p?.attributes || []).filter((a) => a.variation && (a.options || []).length);
  const chosen = p?.type === "variable"
    ? vars.find((v) => attrs.every((a) => {
        const va = (v.attributes || []).find((x) => x.name === a.name);
        return !va || va.option === "" || va.option === pick[a.name]; // "" = "any" in WC
      }) && attrs.every((a) => pick[a.name]))
    : null;
  const allPicked = attrs.every((a) => pick[a.name]);
  const unit = chosen || p;
  const canBuy = p && (p.type !== "variable" ? inStock(p) : (chosen && inStock(chosen)));
  const images = p ? [...(chosen?.image?.src ? [{ src: chosen.image.src, alt: "" }] : []), ...(p.images || [])] : [];
  const desc = htmlToText(p?.short_description) || htmlToText(p?.description);

  const footer = p && (
    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label={t("減少", "Less")} style={qtyBtn}>−</button>
        <span style={{ minWidth: 22, textAlign: "center", fontWeight: 900, color: "#4A4A68" }}>{qty}</span>
        <button type="button" onClick={() => setQty((q) => Math.min(99, q + 1))} aria-label={t("增加", "More")} style={qtyBtn}>＋</button>
      </div>
      <button type="button" disabled={!canBuy} style={{ ...bigBtn(!canBuy), flex: 1 }}
        onClick={() => onAdd({
          key: p.id + ":" + (chosen?.id || 0), productId: p.id, variationId: chosen?.id || null,
          name: htmlToText(p.name) + (chosen ? " – " + (chosen.attributes || []).map((a) => a.option).filter(Boolean).join(" / ") : ""),
          price: Number(unit.price) || 0, image: chosen?.image?.src || p.images?.[0]?.src || "", quantity: qty,
        })}>
        {p.type === "variable" && !allPicked ? t("請先揀選項", "Choose options") : p.type === "variable" && !varsLoaded ? t("載入選項中…", "Loading options…") : !canBuy ? t("售罄", "Sold out") : t("加入購物車", "Add to cart") + " · " + money((Number(unit.price) || 0) * qty)}
      </button>
    </div>
  );

  return (
    <Sheet title={p ? htmlToText(p.name) : t("產品", "Product")} onClose={onClose} footer={footer}>
      {err && <div style={{ color: "#991B1B", fontWeight: 800 }}>{err}</div>}
      {!p && !err && <div style={{ textAlign: "center", color: "#AFAFC4", fontWeight: 800, padding: 30 }}>{t("載入中…", "Loading…")}</div>}
      {p && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {images.length > 0 && (
            <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollSnapType: "x mandatory" }}>
              {images.map((im, i) => (
                <button key={i} type="button" onClick={() => setLb(i)} aria-label={t("放大相片", "Enlarge photo")}
                  style={{ flex: "0 0 " + (images.length > 1 ? "82%" : "100%"), aspectRatio: "1", border: "none", padding: 0, borderRadius: 16, overflow: "hidden", background: "var(--c-lighter)", scrollSnapAlign: "start", cursor: "zoom-in" }}>
                  <img src={im.src} alt={im.alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </button>
              ))}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: "var(--c-primary)" }}>{money(unit.price)}</span>
            {unit.on_sale && unit.regular_price && <span style={{ fontSize: 14, color: "#9CA3AF", textDecoration: "line-through", fontWeight: 700 }}>{money(unit.regular_price)}</span>}
            {isBackorder(unit) && <Tag bg="#7C3AED">{t("接受預訂", "PRE-ORDER")}</Tag>}
            {!inStock(unit) && <Tag bg="#6B7280">{t("售罄", "SOLD OUT")}</Tag>}
          </div>
          {attrs.map((a) => (
            <div key={a.name}>
              <p style={{ fontSize: 12, fontWeight: 900, color: "#8A8A9E", margin: "0 0 6px" }}>{a.name}</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {a.options.map((o) => {
                  const on = pick[a.name] === o;
                  return <button key={o} type="button" onClick={() => setPick((s) => ({ ...s, [a.name]: o }))}
                    style={{ padding: "8px 14px", borderRadius: 12, fontSize: 13, fontWeight: 800, cursor: "pointer", border: on ? "2px solid var(--c-primary)" : "2px solid var(--c-light)", background: on ? "var(--c-lighter)" : "#fff", color: on ? "var(--c-primary)" : "#4A4A68" }}>{o}</button>;
                })}
              </div>
            </div>
          ))}
          {p.type === "variable" && allPicked && !chosen && varsLoaded && <p style={{ fontSize: 12, color: "#B45309", fontWeight: 800, margin: 0 }}>{t("呢個組合暫時冇貨，請揀其他選項。", "That combination isn't available.")}</p>}
          {desc && <p style={{ fontSize: 14, lineHeight: 1.7, color: "#5B5873", margin: 0, whiteSpace: "pre-wrap" }}>{desc}</p>}
        </div>
      )}
      <PhotoLightbox photos={images.map((im) => ({ url: im.src }))} index={lb} onIndex={setLb} onClose={() => setLb(null)} />
    </Sheet>
  );
}

const qtyBtn = { width: 36, height: 36, borderRadius: "50%", border: "2px solid var(--c-light)", background: "#fff", color: "var(--c-primary)", fontSize: 18, fontWeight: 900, cursor: "pointer" };
const field = { width: "100%", boxSizing: "border-box", border: "2px solid var(--c-light)", borderRadius: 12, padding: "11px 14px", fontSize: 15, fontWeight: 600, outline: "none", background: "#FFFDFD", color: "#4A4A68" };

function CartSheet({ tenantId, tenantName, cart, setCart, user, membership, t, onClose }) {
  const [stage, setStage] = useState("cart"); // cart | form | done
  const realEmail = user?.email && !String(user.email).endsWith("@petos.local") ? user.email : "";
  const [f, setF] = useState({
    first: membership?.displayName || user?.name || "", last: "", email: realEmail,
    phone: membership?.phone || user?.phone || "", address: membership?.address || "", city: "", note: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [order, setOrder] = useState(null);
  const subtotal = cart.reduce((n, l) => n + l.price * l.quantity, 0);
  const setQty = (key, q) => setCart((xs) => q <= 0 ? xs.filter((x) => x.key !== key) : xs.map((x) => x.key === key ? { ...x, quantity: Math.min(99, q) } : x));
  const valid = f.first.trim() && /\S+@\S+\.\S+/.test(f.email.trim()) && f.phone.trim();

  const place = async () => {
    if (!valid || busy) return;
    // Open the payment tab NOW, inside the tap — a window.open after the
    // network round-trip is blocked as a popup on Safari.
    let win = null;
    try { win = window.open("", "_blank"); } catch { /* blocked */ }
    setBusy(true); setErr("");
    try {
      const r = await wooCall({}, {
        tenant: tenantId, op: "order",
        line_items: cart.map((l) => ({ product_id: l.productId, ...(l.variationId ? { variation_id: l.variationId } : {}), quantity: l.quantity })),
        billing: { first_name: f.first.trim(), last_name: f.last.trim(), email: f.email.trim(), phone: f.phone.trim(), address_1: f.address.trim(), city: f.city.trim() },
        customer_note: f.note.trim() || undefined,
      });
      setOrder(r); setCart([]); setStage("done");
      if (r?.payment_url) { if (win) win.location.href = r.payment_url; }
      else if (win) win.close();
    } catch (e) {
      if (win) win.close();
      setErr(e.message || t("落單失敗，請再試", "Order failed — please try again"));
    } finally { setBusy(false); }
  };

  const title = stage === "cart" ? t("購物車", "Cart") + ` (${cart.reduce((n, l) => n + l.quantity, 0)})` : stage === "form" ? t("結帳", "Checkout") : t("已下單", "Order placed");
  const footer = stage === "cart" && cart.length > 0
    ? <button type="button" style={bigBtn(false)} onClick={() => setStage("form")}>{t("繼續結帳", "Continue to checkout")} · {money(subtotal)}</button>
    : stage === "form"
      ? <button type="button" style={bigBtn(!valid || busy)} disabled={!valid || busy} onClick={place}>{busy ? t("處理中…", "Processing…") : "💳 " + t("落單 · 前往付款", "Place order · Pay")}</button>
      : null;

  return (
    <Sheet title={title} onClose={onClose} footer={footer}>
      {stage === "cart" && (cart.length === 0
        ? <div style={{ textAlign: "center", padding: "36px 10px", color: "#8A8A9E", fontWeight: 800 }}>🛒<br />{t("購物車係空嘅", "Your cart is empty")}</div>
        : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {cart.map((l) => (
              <div key={l.key} style={{ display: "flex", gap: 10, alignItems: "center", background: "#fff", border: "2px solid var(--c-light)", borderRadius: 14, padding: 10 }}>
                {l.image ? <img src={l.image} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} /> : <div style={{ width: 56, height: 56, borderRadius: 10, background: "var(--c-lighter)", flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#4A4A68", lineHeight: 1.35 }}>{l.name}</div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#8A8A9E", marginTop: 2 }}>{money(l.price)} × {l.quantity}</div>
                </div>
                <button type="button" onClick={() => setQty(l.key, l.quantity - 1)} aria-label={t("減少", "Less")} style={qtyBtn}>−</button>
                <button type="button" onClick={() => setQty(l.key, l.quantity + 1)} aria-label={t("增加", "More")} style={qtyBtn}>＋</button>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, color: "#4A4A68", padding: "4px 2px" }}><span>{t("小計", "Subtotal")}</span><span style={{ color: "var(--c-primary)", fontSize: 18 }}>{money(subtotal)}</span></div>
            <p style={{ fontSize: 11, color: "#AFAFC4", fontWeight: 700, margin: 0 }}>{t("運費同稅項會喺付款頁計算。", "Shipping and tax are calculated at payment.")}</p>
          </div>)}

      {stage === "form" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[["first", t("名字 *", "First name *"), "text", "given-name"], ["last", t("姓氏", "Last name"), "text", "family-name"], ["email", t("電郵 *（收訂單確認）", "Email * (order confirmation)"), "email", "email"], ["phone", t("電話 *", "Phone *"), "tel", "tel"], ["address", t("送貨地址", "Delivery address"), "text", "street-address"], ["city", t("城市 / 地區", "City / area"), "text", "address-level2"], ["note", t("備註（可選）", "Note (optional)"), "text", "off"]].map(([k, label, type, ac]) => (
            <label key={k} style={{ display: "block" }}>
              <span style={{ display: "block", fontSize: 12, fontWeight: 900, color: "#8A8A9E", margin: "0 0 4px" }}>{label}</span>
              <input type={type} autoComplete={ac} value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} style={field} />
            </label>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, color: "#4A4A68", padding: "4px 2px" }}><span>{t("小計", "Subtotal")}</span><span style={{ color: "var(--c-primary)" }}>{money(subtotal)}</span></div>
          <p style={{ fontSize: 11.5, color: "#8A8A9E", fontWeight: 700, margin: 0, lineHeight: 1.5 }}>{t("落單後會開啟「" + (tenantName || "店舖") + "」嘅付款頁面，喺嗰度揀付款方式同完成付款。", "After placing the order, the shop's payment page opens so you can pay there.")}</p>
          {err && <div style={{ background: "#FEE2E2", color: "#991B1B", borderRadius: 12, padding: "10px 12px", fontSize: 13, fontWeight: 800 }}>{err}</div>}
          <button type="button" onClick={() => setStage("cart")} style={{ background: "none", border: "none", color: "#8A8A9E", fontWeight: 800, cursor: "pointer", padding: 6 }}>← {t("返回購物車", "Back to cart")}</button>
        </div>
      )}

      {stage === "done" && (
        <div style={{ textAlign: "center", padding: "20px 6px", display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <div style={{ width: 76, height: 76, borderRadius: "50%", background: "#D1FAE5", color: "#065F46", fontSize: 34, display: "flex", alignItems: "center", justifyContent: "center" }}>✓</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#4A4A68" }}>{t("已下單！", "Order placed!")}</div>
          {order && <div style={{ fontSize: 13, fontWeight: 800, color: "#8A8A9E" }}>{t("訂單編號", "Order #")} <span style={{ color: "var(--c-primary)", fontSize: 18 }}>{order.number || order.id}</span></div>}
          <p style={{ fontSize: 13, color: "#5B5873", fontWeight: 700, lineHeight: 1.6, margin: 0 }}>{t("請喺新開嘅付款頁完成付款。如果冇自動開啟，撳下面掣。", "Finish paying on the payment page that opened. If it didn't open, tap below.")}</p>
          {order?.payment_url && <a href={order.payment_url} target="_blank" rel="noopener noreferrer" style={{ ...bigBtn(false), display: "block", textDecoration: "none", boxSizing: "border-box" }}>💳 {t("前往付款", "Go to payment")}</a>}
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: "#8A8A9E", fontWeight: 800, cursor: "pointer", padding: 6 }}>{t("完成", "Done")}</button>
        </div>
      )}
    </Sheet>
  );
}
